import {GraphPair, ISerializable} from './base';
import moment from 'moment';
import {DropzoneConfigInterface} from 'ngx-dropzone-wrapper';
import {ContainerFile} from './project';
import { BoardStrength, BoardType, BoxType, PaperType, SecondaryPaperType, Subcategory, User } from '@company-box/models';

export class QuoteItem implements ISerializable<QuoteItem> {

    id: number | undefined;
    name: string | undefined;
    length: number | undefined;
    width: number | undefined;
    depth: number | undefined;
    blanklength: number | undefined;
    blankwidth: number | undefined;
    knockdepth: number | undefined;
    knocklength: number | undefined;
    knockwidth: number | undefined;
    label: string | undefined;
    boxCode: string | undefined;
    flute: string | undefined;
    boardStrength: BoardStrength | undefined;
    paperType: PaperType | undefined;
    secondaryPaperType: SecondaryPaperType | undefined;
    subcategory: Subcategory | undefined;
    boardType: BoardType | undefined;
    gloss: boolean | undefined;
    luxe: boolean | undefined;
    preset: boolean | undefined;
    quantity: number | undefined;
    twoSided: boolean | undefined;
    expedited: boolean | undefined;

    container: string | undefined;
    insideGraphics: ContainerFile | undefined;
    outsideGraphics: ContainerFile | undefined;

    pricePerUnit: number | undefined;
    totalPrice: number | undefined;
    type: BoxType | undefined;
    options: string | undefined;
    rush: boolean | undefined;
    taxable: boolean | undefined = true;

    get dimensions() {
        if (this.label) {
            return this.label;
        }
        if (this.length && this.width && this.depth) {
            return this.length + 'x' + this.width + 'x' + this.depth;
        }
        return undefined;
    }

    get hasGraphics(): boolean {
        if (this.twoSided) {
            return this.insideGraphics !== undefined && this.outsideGraphics !== undefined;
        } else {
            return this.insideGraphics !== undefined || this.outsideGraphics !== undefined;
        }
    }

    deserialize(input: any): QuoteItem {
        this.id = input.id;
        this.name = input.name;
        this.length = input.length;
        this.width = input.width;
        this.depth = input.depth;
        this.label = input.label;
        this.blanklength = input.blanklength;
        this.blankwidth = input.blankwidth;
        this.knockdepth = input.knockdepth;
        this.knocklength = input.knocklength;
        this.knockwidth = input.knockwidth;
        this.boxCode = input.boxCode;
        this.boardType = input.boardType;
        this.boardStrength = input.boardStrength;
        if (input.paperType) {
            // console.log('paperType', input.paperType, typeof input.paperType);
            if (typeof input.paperType === 'string') {
                this.paperType = new PaperType().deserialize({id: input.paperType});
            } else {
                this.paperType = input.paperType;
            }
        }
        if (input.secondaryPaperType) {
            // console.log('secondary', input.secondaryPaperType, typeof input.secondaryPaperType);
            if (typeof input.secondaryPaperType === 'string') {
                this.secondaryPaperType = new SecondaryPaperType().deserialize({id: input.secondaryPaperType});
            } else {
                this.secondaryPaperType = input.secondaryPaperType;
            }
        }
        this.gloss = input.gloss;
        this.luxe = input.luxe;
        this.preset = input.preset;
        this.quantity = input.quantity;
        this.twoSided = input.twoSided;
        this.expedited = input.expedited;
        if (input.type) {
            this.type = new BoxType().deserialize(input.type);
        }
        if (input.subcategory) {
            this.subcategory = new Subcategory().deserialize(input.subcategory);
        }
        if (input.boardType && this.subcategory) {
            this.subcategory.boardTypes.forEach(value => {
                if (value.flute === input.boardType) {
                    this.boardType = value;
                }
            });
        }
        // console.log(this.boardType);
        if (input.boardStrength && this.boardType) {
            if(this.boardType.boardStrengths){
            this.boardType.boardStrengths.forEach(value => {
                if (value.code === input.boardStrength) {
                    this.boardStrength = value;
                }
            });
        }else{
            this.boardStrength=input.boardStrength;
        }
        }

        if (this.type) {
            this.type.subcategory = this.subcategory;
        }
        if (this.type && this.boardType) {
            this.type.boardType = this.boardType;
        }

        this.container = input.container;

        this.pricePerUnit = input.pricePerUnit;
        this.totalPrice = input.totalPrice;
        this.taxable = input.taxable;

        return this;
    }
}

export class QuoteItemWrapper extends QuoteItem {
    updating: boolean | undefined;
    insideDesignUploadConfig: DropzoneConfigInterface | undefined;
    outsideDesignUploadConfig: DropzoneConfigInterface | undefined;
    insideDropzoneHidden: boolean | undefined;
    outsideDropzoneHidden: boolean | undefined;
}

export class Quote {

    id: number | undefined;
    name: string | undefined;
    notes: string | undefined;
    user: User | undefined;
    validUntil: Date | undefined;
    items: QuoteItem[] = [];
    draftOrderId: string | undefined;
    orderId: string | undefined;
    orderDate: Date | undefined;
    poNumber: string | undefined;

    get isExpired(): boolean {
        return !this.orderId && moment(this.validUntil).isBefore(new Date());
    }

    get totalQuantity(): number | undefined {
        if (!this.items || this.items.length === 0) {
            // console.log('checkout  is empty');
            return undefined;
        }
        return this.items
            .map(value => value.quantity)
            .reduce((previousValue, currentValue) => (previousValue ?? 0) + (currentValue ?? 0));
    }

    get totalPrice(): number {
        if (!this.items || this.items.length === 0) {
            return 0;
        }
        return this.items
            .map(quote => quote.totalPrice ?? 0)
            .reduce((previousValue, currentValue) => previousValue + currentValue);
    }

    get hasGraphics(): boolean {
        return this.items.map(value => value.hasGraphics).reduce((previousValue, currentValue) => previousValue && currentValue);
    }

    deserialize(input: any): Quote {
        this.id = input.id;
        this.name = input.name;
        this.notes = input.notes;
        this.draftOrderId = input.draftOrderId;
        if (input.user) {
            this.user = new User().deserialize(input.user);
        }
        this.orderId = input.orderId;
        this.poNumber = input.poNumber;
        if (input.orderDate) {
            this.orderDate = moment(input.orderDate).toDate();
        }
        if (input.validUntil) {
            this.validUntil = new Date(input.validUntil);
        }
        this.items = [];
        if (input.items) {
            (input.items as any[]).forEach((item) => {
                this.items.push(new QuoteItemWrapper().deserialize(item));
            });
        }
        return this;
    }
}

export class QuoteGraphData implements ISerializable<QuoteGraphData> {

    days: number | undefined;
    totalCount: number | undefined;
    totalValue: number | undefined;
    quoteCounts: GraphPair<number>[] = [];
    quoteValues: GraphPair<number>[] = [];

    deserialize(input: any): QuoteGraphData {
        this.days = input.days;
        this.totalCount = input.totalCount;
        this.totalValue = input.totalValue;
        this.quoteCounts = [];
        if (input.quoteCounts) {
            (input.quoteCounts as any[]).forEach((object) => {
                const p: GraphPair<number> = new GraphPair<number>().deserialize(object);
                this.quoteCounts.push(p);
            });
        }
        this.quoteValues = [];
        if (input.quoteValues) {
            (input.quoteValues as any[]).forEach((object) => {
                const p: GraphPair<number> = new GraphPair<number>().deserialize(object);
                this.quoteValues.push(p);
            });
        }
        return this;
    }

}
